/**
 * Dates et mots de passe générés : deux utilitaires sans état.
 */

/**
 * D43 : la locale passée à `Intl`. L'arabe s'affiche en chiffres LATINS (usage
 * marocain et administratif, décidé le 18 octobre) : `ar` seul donnerait les
 * chiffres indo-arabes (٠١٢), d'où `ar-MA` et l'extension `nu-latn` explicite.
 */
export function intlLocale(locale: string): string {
    return locale === 'ar' ? 'ar-MA-u-nu-latn' : locale;
}

/** Une date ISO 8601 rendue dans la langue servie ; `null` reste `null`. */
export function formatDateTime(iso: string | null, locale: string): string | null {
    if (iso === null) {
        return null;
    }

    const date = new Date(iso);

    return Number.isNaN(date.getTime()) ? iso : new Intl.DateTimeFormat(intlLocale(locale), { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function formatDate(iso: string | null, locale: string): string | null {
    if (iso === null) {
        return null;
    }

    // Une DATE seule (« 2026-09-01 ») est un jour du calendrier, pas un instant :
    // lue comme minuit UTC, elle s'afficherait la veille à l'ouest de Greenwich.
    const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    const date = day === null ? new Date(iso) : new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));

    return Number.isNaN(date.getTime()) ? iso : new Intl.DateTimeFormat(intlLocale(locale), { dateStyle: 'medium' }).format(date);
}

/**
 * Un mot de passe provisoire à dicter ou recopier : 16 caractères, sans les
 * signes qu'on confond à la lecture (0/O, 1/l/I), tirés par
 * `crypto.getRandomValues` avec REJET — un simple modulo favoriserait les
 * premiers caractères de l'alphabet.
 */
const ALPHABET = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export function generatePassword(length = 16): string {
    const limit = 256 - (256 % ALPHABET.length);
    const out: string[] = [];
    const buffer = new Uint8Array(length * 2);

    while (out.length < length) {
        crypto.getRandomValues(buffer);

        for (const byte of buffer) {
            if (byte < limit && out.length < length) {
                out.push(ALPHABET.charAt(byte % ALPHABET.length));
            }
        }
    }

    return out.join('');
}
